/**
 * What this browser remembers for each person (F4 (a), settled 2026-10-05):
 * the theme, the text size, and whether location is sent. **Per browser, and
 * keyed by account** — two people on one laptop each keep their own, as the
 * offline cache is theirs (25B rule 7).
 *
 * `localStorage` can throw (a private window, blocked site data): every read
 * falls back to the default and every write is best effort.
 */
import { create } from "zustand";

import { DEFAULT_SCALE, parseScale, stepScale } from "./textScale";

export const THEMES = ["system", "light", "dark"] as const;
export type Theme = (typeof THEMES)[number];

const key = (account: number | null, name: string) => `kindred:${account ?? "signed-out"}:${name}`;

/** A per-browser value, kept per account (F4 (a)). */
export function read(account: number | null, name: string): string | null {
  try {
    return localStorage.getItem(key(account, name));
  } catch {
    return null;
  }
}

export function write(account: number | null, name: string, value: string): void {
  try {
    localStorage.setItem(key(account, name), value);
  } catch {
    // Nothing to do: the choice holds until the page closes.
  }
}

/**
 * "system" stamps nothing, which is what lets the generated
 * `prefers-color-scheme` block apply at all; light and dark win either way.
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", theme);
}

/** What is on screen, with "system" resolved. */
export function resolvedTheme(theme: Theme): "light" | "dark" {
  if (theme !== "system") return theme;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyScale(scale: number): void {
  document.documentElement.style.setProperty("--prose-scale", String(scale));
}

interface Prefs {
  account: number | null;
  theme: Theme;
  textScale: number;
  location: boolean;
  /** The person chose on or off here. A cache clear forgets it; only then does
   * a held grant turn location back on (v1.82) — an explicit "off" stays off. */
  locationChosen: boolean;
  /** Read this person's choices, and show them. */
  bind(account: number | null): void;
  setTheme(theme: Theme): void;
  /** Flip what is on screen — from "system" too. */
  toggleTheme(): void;
  stepText(steps: number): void;
  resetText(): void;
  setLocation(on: boolean): void;
}

export const usePrefs = create<Prefs>()((set, get) => ({
  account: null,
  theme: "system",
  textScale: DEFAULT_SCALE,
  location: false,
  locationChosen: false,
  bind(account) {
    const stored = read(account, "theme");
    const theme = (THEMES as readonly string[]).includes(stored ?? "")
      ? (stored as Theme)
      : "system";
    const textScale = parseScale(read(account, "text-scale"));
    const where = read(account, "location");
    applyTheme(theme);
    applyScale(textScale);
    set({ account, theme, textScale, location: where === "on", locationChosen: where !== null });
  },
  setTheme(theme) {
    write(get().account, "theme", theme);
    applyTheme(theme);
    set({ theme });
  },
  toggleTheme() {
    get().setTheme(resolvedTheme(get().theme) === "dark" ? "light" : "dark");
  },
  stepText(steps) {
    const textScale = stepScale(get().textScale, steps);
    write(get().account, "text-scale", String(textScale));
    applyScale(textScale);
    set({ textScale });
  },
  resetText() {
    write(get().account, "text-scale", String(DEFAULT_SCALE));
    applyScale(DEFAULT_SCALE);
    set({ textScale: DEFAULT_SCALE });
  },
  setLocation(on) {
    write(get().account, "location", on ? "on" : "off");
    set({ location: on, locationChosen: true });
  },
}));
